import {
  mysqlTable,
  int,
  bigint,
  tinyint,
  varchar,
  text,
  mediumtext,
  longtext,
  double,
  decimal,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies, companyAddresses } from "./tenancy";
import { users } from "./identity";
import { projects } from "./projects";
import { orders } from "./core";

/**
 * Batch 1.7 — Finance (reskin-docs/00). Money/economics = Admin/HR only (reskin-docs/07 §6).
 * Ported by reconciling each table's Schema::create + every up() Schema::table ALTER across
 * the worksuite migrations (NOT just create blocks). Source: 2018_01_01_..._create_worksuite_new_table.php
 * plus later migrations. Notable reconciliations applied to the FINAL shape:
 *   - currencies: company_id nullable (no FK in legacy create — added later? no — kept w/ FK to companies via tenancy).
 *   - invoices/estimates/credit_notes/invoice_recurring/estimate_templates/products get `unit_id`
 *     (2023_01_20 unit_types) → FK unit_types SET NULL.
 *   - invoices/payments/expenses get `default_currency_id` (FK currencies cascade) + `exchange_rate`
 *     double nullable (2022_12_29).
 *   - invoices: invoice_number/estimate_number/cn_number left varchar (changed to bigInteger then
 *     back to string by 2023_09_25) + original_*_number; +bank_account_id (2022_09_20),
 *     +invoice_payment_id (FK invoice_payment_details, 2024_09_12), +payment_status enum['1','0']
 *     default '0' / +offline_method_id / +transaction_id / +gateway (2023_03_24). invoice_recurring_id
 *     FK changed to SET NULL (2022_08_26).
 *   - estimates: +project_id (2025_02_03), +estimate_request_id (2024_07_01), +original_estimate_number.
 *   - contracts: +contract_note (2022_11_16) after alternate_address; company_logo DROPPED (2023_01_09);
 *     +project_id (2023_01_09); +company_sign/sign_date (2023_02_01, sign_date later dateTime 2023_11_23);
 *     +sign_by FK users SET NULL (2024_09_16); +contract_number/original_contract_number (2023_09_25).
 *   - contract_signs: +place/+date (2023_02_01). NO company_id (legacy).
 *   - expenses_recurring/invoice_recurring: +issue_date/next_*_date/immediate_* + bank_account_id added
 *     then DROPPED in down — those adds live in up(), kept. (2023_01_23, 2023_04_11).
 *   - Money/precision: amount/sub_total/total/discount/unit_price/quantity bumped to double(30,2)
 *     by 2023_12_19 — final precision applied here.
 * Deferred cross-batch FKs (kept typed, no .references(), recorded at bottom):
 *   projects, clients/users-as-client (FK→users wired), orders, expenses↔roles, etc.
 */

/* ─────────────────────────── CURRENCIES (FK target for many) ─────────────────────────── */

export const currencies = mysqlTable("currencies", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  currencyName: varchar("currency_name", { length: 255 }).notNull(),
  currencySymbol: varchar("currency_symbol", { length: 255 }),
  currencyCode: varchar("currency_code", { length: 255 }).notNull(),
  exchangeRate: double("exchange_rate"),
  isCryptocurrency: mysqlEnum("is_cryptocurrency", ["yes", "no"]).default("no").notNull(),
  usdPrice: double("usd_price"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── TAXES ─────────────────────────── */

/** taxes — softDeletes. */
export const taxes = mysqlTable("taxes", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  taxName: varchar("tax_name", { length: 255 }).notNull(),
  ratePercent: varchar("rate_percent", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
  deletedAt: timestamp("deleted_at"),
});

/* ─────────────────────────── UNIT TYPES ─────────────────────────── */

export const unitTypes = mysqlTable("unit_types", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  unitType: varchar("unit_type", { length: 255 }).notNull(),
  isDefault: boolean("default").default(false).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── ESTIMATES ─────────────────────────── */

export const estimates = mysqlTable(
  "estimates",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    clientId: int("client_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
    estimateNumber: varchar("estimate_number", { length: 255 }),
    originalEstimateNumber: varchar("original_estimate_number", { length: 255 }),
    validTill: date("valid_till").notNull(),
    subTotal: double("sub_total", { precision: 30, scale: 2 }).notNull(),
    discount: double("discount", { precision: 30, scale: 2 }).default(0).notNull(),
    discountType: mysqlEnum("discount_type", ["percent", "fixed"]).default("percent").notNull(),
    total: double("total", { precision: 30, scale: 2 }).notNull(),
    currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    status: mysqlEnum("status", [
      "declined",
      "accepted",
      "waiting",
      "sent",
      "draft",
      "canceled",
    ])
      .default("waiting")
      .notNull(),
    note: mediumtext("note"),
    description: longtext("description"),
    sendStatus: boolean("send_status").default(true).notNull(),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    hash: text("hash"),
    calculateTax: mysqlEnum("calculate_tax", ["after_discount", "before_discount"])
      .default("after_discount")
      .notNull(),
    unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → projects
    estimateRequestId: bigint("estimate_request_id", { mode: "number", unsigned: true }), // FK → estimate_requests (own file, but nullable add; left typed)
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("estimates_project_id_foreign").on(t.projectId)],
);

export const estimateItems = mysqlTable("estimate_items", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  estimateId: int("estimate_id", { unsigned: true })
    .notNull()
    .references(() => estimates.id, { onDelete: "cascade", onUpdate: "cascade" }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  itemSummary: text("item_summary"),
  type: mysqlEnum("type", ["item", "discount", "tax"]).default("item").notNull(),
  quantity: double("quantity", { precision: 30, scale: 2 }).notNull(),
  unitPrice: double("unit_price", { precision: 30, scale: 2 }).notNull(),
  amount: double("amount", { precision: 30, scale: 2 }).notNull(),
  taxes: varchar("taxes", { length: 255 }),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const estimateItemImages = mysqlTable("estimate_item_images", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  estimateItemId: int("estimate_item_id", { unsigned: true })
    .notNull()
    .references(() => estimateItems.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** accept_estimates — bigIncrements id. */
export const acceptEstimates = mysqlTable("accept_estimates", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  estimateId: int("estimate_id", { unsigned: true })
    .notNull()
    .references(() => estimates.id, { onDelete: "cascade", onUpdate: "cascade" }),
  fullName: varchar("full_name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  signature: varchar("signature", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── ESTIMATE TEMPLATES ─────────────────────────── */

export const estimateTemplates = mysqlTable("estimate_templates", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  subTotal: double("sub_total", { precision: 30, scale: 2 }).notNull(),
  total: double("total", { precision: 30, scale: 2 }).notNull(),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  discountType: mysqlEnum("discount_type", ["percent", "fixed"]).notNull(),
  discount: double("discount", { precision: 30, scale: 2 }).notNull(),
  invoiceConvert: boolean("invoice_convert").default(false).notNull(),
  status: mysqlEnum("status", ["declined", "accepted", "waiting"]).default("waiting").notNull(),
  note: mediumtext("note"),
  description: longtext("description"),
  calculateTax: mysqlEnum("calculate_tax", ["after_discount", "before_discount"])
    .default("after_discount")
    .notNull(),
  clientComment: text("client_comment"),
  signatureApproval: boolean("signature_approval").default(true).notNull(),
  hash: text("hash"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const estimateTemplateItems = mysqlTable("estimate_template_items", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  estimateTemplateId: bigint("estimate_template_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => estimateTemplates.id, { onDelete: "cascade", onUpdate: "cascade" }),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  type: mysqlEnum("type", ["item", "discount", "tax"]).default("item").notNull(),
  quantity: tinyint("quantity").notNull(),
  unitPrice: double("unit_price").notNull(),
  amount: double("amount").notNull(),
  itemSummary: text("item_summary"),
  taxes: varchar("taxes", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const estimateTemplateItemImages = mysqlTable("estimate_template_item_images", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  estimateTemplateItemId: int("estimate_template_item_id", { unsigned: true })
    .notNull()
    .references(() => estimateTemplateItems.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── ESTIMATE REQUESTS ─────────────────────────── */

export const estimateRequests = mysqlTable("estimate_requests", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  estimateRequestNumber: varchar("estimate_request_number", { length: 255 }),
  originalRequestNumber: varchar("original_request_number", { length: 255 }),
  clientId: int("client_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  estimateId: int("estimate_id", { unsigned: true }).references(() => estimates.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  description: longtext("description").notNull(),
  estimatedBudget: double("estimated_budget", { precision: 16, scale: 2 }).notNull(),
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  earlyRequirement: text("early_requirement"),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  status: mysqlEnum("status", ["pending", "rejected", "accepted", "in process"])
    .default("pending")
    .notNull(),
  reason: text("reason").notNull(),
  addedBy: int("added_by", { unsigned: true }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── INVOICE RECURRING ─────────────────────────── */

export const invoiceRecurring = mysqlTable("invoice_recurring", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  clientId: int("client_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  issueDate: date("issue_date").notNull(),
  nextInvoiceDate: date("next_invoice_date"),
  dueDate: date("due_date").notNull(),
  subTotal: double("sub_total").default(0).notNull(),
  total: double("total").default(0).notNull(),
  discount: double("discount").default(0).notNull(),
  discountType: mysqlEnum("discount_type", ["percent", "fixed"]).default("percent").notNull(),
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(),
  file: varchar("file", { length: 255 }),
  fileOriginalName: varchar("file_original_name", { length: 255 }),
  note: text("note"),
  showShippingAddress: mysqlEnum("show_shipping_address", ["yes", "no"]).default("no").notNull(),
  dayOfMonth: int("day_of_month").default(1),
  dayOfWeek: int("day_of_week").default(1),
  paymentMethod: varchar("payment_method", { length: 255 }),
  rotation: mysqlEnum("rotation", [
    "monthly",
    "weekly",
    "bi-weekly",
    "quarterly",
    "half-yearly",
    "annually",
    "daily",
  ]).notNull(),
  billingCycle: int("billing_cycle"),
  clientCanStop: boolean("client_can_stop").default(true).notNull(),
  unlimitedRecurring: boolean("unlimited_recurring").default(false).notNull(),
  immediateInvoice: boolean("immediate_invoice").default(false).notNull(),
  deletedAt: datetime("deleted_at"),
  shippingAddress: text("shipping_address"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  calculateTax: mysqlEnum("calculate_tax", ["after_discount", "before_discount"])
    .default("after_discount")
    .notNull(),
  unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  bankAccountId: int("bank_account_id", { unsigned: true }).references(
    (): AnyMySqlColumn => bankAccounts.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const invoiceRecurringItems = mysqlTable("invoice_recurring_items", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  invoiceRecurringId: bigint("invoice_recurring_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => invoiceRecurring.id, { onDelete: "cascade", onUpdate: "cascade" }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  quantity: double("quantity", { precision: 30, scale: 2 }).notNull(),
  unitPrice: double("unit_price", { precision: 30, scale: 2 }).notNull(),
  amount: double("amount", { precision: 30, scale: 2 }).notNull(),
  taxes: text("taxes"),
  type: mysqlEnum("type", ["item", "discount", "tax"]).default("item").notNull(),
  itemSummary: text("item_summary"),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const invoiceRecurringItemImages = mysqlTable("invoice_recurring_item_images", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  invoiceRecurringItemId: int("invoice_recurring_item_id", { unsigned: true })
    .notNull()
    .references(() => invoiceRecurringItems.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── OFFLINE PAYMENT METHODS ─────────────────────────── */

export const offlinePaymentMethods = mysqlTable("offline_payment_methods", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  description: longtext("description"),
  status: mysqlEnum("status", ["yes", "no"]).default("yes"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── INVOICE PAYMENT DETAILS ─────────────────────────── */

export const invoicePaymentDetails = mysqlTable("invoice_payment_details", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  title: varchar("title", { length: 255 }).notNull(),
  paymentDetails: text("payment_details"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── INVOICES ─────────────────────────── */

export const invoices = mysqlTable(
  "invoices",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → projects
    clientId: int("client_id", { unsigned: true }).references(() => users.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    orderId: bigint("order_id", { mode: "number", unsigned: true }).references(() => orders.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }), // FK → orders
    invoiceNumber: varchar("invoice_number", { length: 255 }).notNull(),
    originalInvoiceNumber: varchar("original_invoice_number", { length: 255 }),
    issueDate: date("issue_date").notNull(),
    dueDate: date("due_date").notNull(),
    subTotal: double("sub_total", { precision: 30, scale: 2 }).notNull(),
    discount: double("discount", { precision: 30, scale: 2 }).default(0).notNull(),
    discountType: mysqlEnum("discount_type", ["percent", "fixed"]).default("percent").notNull(),
    total: double("total", { precision: 30, scale: 2 }).notNull(),
    currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    defaultCurrencyId: int("default_currency_id", { unsigned: true }).references(
      () => currencies.id,
      { onDelete: "cascade", onUpdate: "cascade" },
    ),
    exchangeRate: double("exchange_rate"),
    status: mysqlEnum("status", ["paid", "unpaid", "partial", "canceled", "draft"])
      .default("unpaid")
      .notNull(),
    recurring: mysqlEnum("recurring", ["yes", "no"]).default("no").notNull(),
    billingCycle: int("billing_cycle"),
    billingInterval: int("billing_interval"),
    billingFrequency: varchar("billing_frequency", { length: 255 }),
    file: varchar("file", { length: 255 }),
    fileOriginalName: varchar("file_original_name", { length: 255 }),
    note: text("note"),
    creditNote: boolean("credit_note").default(false).notNull(),
    showShippingAddress: mysqlEnum("show_shipping_address", ["yes", "no"]).default("no").notNull(),
    estimateId: int("estimate_id", { unsigned: true }).references(() => estimates.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    sendStatus: boolean("send_status").default(true).notNull(),
    dueAmount: double("due_amount", { precision: 30, scale: 2 }).default(0).notNull(),
    parentId: int("parent_id", { unsigned: true }).references((): AnyMySqlColumn => invoices.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    // invoice_recurring_id FK relaxed to SET NULL by 2022_08_26
    invoiceRecurringId: bigint("invoice_recurring_id", {
      mode: "number",
      unsigned: true,
    }).references(() => invoiceRecurring.id, { onDelete: "set null", onUpdate: "cascade" }),
    createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
      onDelete: "no action",
      onUpdate: "cascade",
    }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    hash: text("hash"),
    calculateTax: mysqlEnum("calculate_tax", ["after_discount", "before_discount"])
      .default("after_discount")
      .notNull(),
    companyAddressId: bigint("company_address_id", { mode: "number", unsigned: true }).references(
      () => companyAddresses.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    eventId: text("event_id"),
    customInvoiceNumber: varchar("custom_invoice_number", { length: 255 }),
    paymentStatus: mysqlEnum("payment_status", ["1", "0"]).default("0").notNull(),
    offlineMethodId: int("offline_method_id", { unsigned: true }).references(
      () => offlinePaymentMethods.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    transactionId: varchar("transaction_id", { length: 255 }),
    invoicePaymentId: bigint("invoice_payment_id", { mode: "number", unsigned: true }).references(
      () => invoicePaymentDetails.id,
      { onDelete: "cascade", onUpdate: "cascade" },
    ),
    gateway: varchar("gateway", { length: 255 }),
    unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    bankAccountId: int("bank_account_id", { unsigned: true }).references(
      (): AnyMySqlColumn => bankAccounts.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("invoices_due_date_index").on(t.dueDate)],
);

export const invoiceItems = mysqlTable("invoice_items", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  invoiceId: int("invoice_id", { unsigned: true })
    .notNull()
    .references(() => invoices.id, { onDelete: "cascade", onUpdate: "cascade" }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  itemSummary: text("item_summary"),
  type: mysqlEnum("type", ["item", "discount", "tax"]).default("item").notNull(),
  quantity: double("quantity", { precision: 30, scale: 2 }).notNull(),
  unitPrice: double("unit_price", { precision: 30, scale: 2 }).notNull(),
  amount: double("amount", { precision: 30, scale: 2 }).notNull(),
  taxes: varchar("taxes", { length: 255 }),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const invoiceItemImages = mysqlTable("invoice_item_images", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  invoiceItemId: int("invoice_item_id", { unsigned: true })
    .notNull()
    .references(() => invoiceItems.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const invoiceFiles = mysqlTable("invoice_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  invoiceId: int("invoice_id", { unsigned: true })
    .notNull()
    .references(() => invoices.id, { onDelete: "cascade", onUpdate: "cascade" }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  filename: varchar("filename", { length: 200 }),
  hashname: varchar("hashname", { length: 200 }),
  size: varchar("size", { length: 200 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── CREDIT NOTES ─────────────────────────── */

export const creditNotes = mysqlTable("credit_notes", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  clientId: int("client_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  cnNumber: varchar("cn_number", { length: 255 }).notNull(),
  originalCreditNoteNumber: varchar("original_credit_note_number", { length: 255 }),
  invoiceId: int("invoice_id", { unsigned: true }),
  issueDate: date("issue_date").notNull(),
  dueDate: date("due_date").notNull(),
  discount: double("discount", { precision: 30, scale: 2 }).default(0).notNull(),
  discountType: mysqlEnum("discount_type", ["percent", "fixed"]).default("percent").notNull(),
  subTotal: double("sub_total", { precision: 30, scale: 2 }).notNull(),
  total: double("total", { precision: 30, scale: 2 }).notNull(),
  adjustmentAmount: double("adjustment_amount", { precision: 30, scale: 2 }),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  status: mysqlEnum("status", ["closed", "open"]).default("open").notNull(),
  recurring: mysqlEnum("recurring", ["yes", "no"]).default("no").notNull(),
  billingFrequency: varchar("billing_frequency", { length: 255 }),
  billingInterval: int("billing_interval"),
  billingCycle: int("billing_cycle"),
  file: varchar("file", { length: 255 }),
  fileOriginalName: varchar("file_original_name", { length: 255 }),
  note: text("note"),
  deletedAt: timestamp("deleted_at"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  calculateTax: mysqlEnum("calculate_tax", ["after_discount", "before_discount"])
    .default("after_discount")
    .notNull(),
  unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const creditNoteItems = mysqlTable("credit_note_items", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  creditNoteId: int("credit_note_id", { unsigned: true })
    .notNull()
    .references(() => creditNotes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  type: mysqlEnum("type", ["item", "discount", "tax"]).default("item").notNull(),
  quantity: int("quantity").notNull(),
  unitPrice: double("unit_price", { precision: 30, scale: 2 }).notNull(),
  amount: double("amount", { precision: 30, scale: 2 }).notNull(),
  taxes: varchar("taxes", { length: 255 }),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  itemSummary: text("item_summary"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const creditNoteItemImages = mysqlTable("credit_note_item_images", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  creditNoteItemId: int("credit_note_item_id", { unsigned: true })
    .notNull()
    .references(() => creditNoteItems.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  externalLink: varchar("external_link", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── EXPENSES ─────────────────────────── */

export const expensesCategory = mysqlTable("expenses_category", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryName: varchar("category_name", { length: 255 }).notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const expensesRecurring = mysqlTable("expenses_recurring", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryId: bigint("category_id", { mode: "number", unsigned: true }).references(
    () => expensesCategory.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  userId: int("user_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
    onDelete: "no action",
    onUpdate: "cascade",
  }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  dayOfMonth: int("day_of_month").default(1),
  dayOfWeek: int("day_of_week").default(1),
  paymentMethod: varchar("payment_method", { length: 255 }),
  rotation: mysqlEnum("rotation", [
    "monthly",
    "weekly",
    "bi-weekly",
    "quarterly",
    "half-yearly",
    "annually",
    "daily",
  ]).notNull(),
  billingCycle: int("billing_cycle"),
  issueDate: date("issue_date").notNull(),
  nextExpenseDate: date("next_expense_date"),
  unlimitedRecurring: boolean("unlimited_recurring").default(false).notNull(),
  price: double("price").notNull(),
  bill: varchar("bill", { length: 255 }),
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(),
  description: text("description"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  purchaseFrom: varchar("purchase_from", { length: 255 }),
  immediateExpense: boolean("immediate_expense").default(false).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const expenses = mysqlTable("expenses", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  itemName: varchar("item_name", { length: 255 }).notNull(),
  purchaseDate: date("purchase_date").notNull(),
  purchaseFrom: varchar("purchase_from", { length: 255 }),
  price: double("price", { precision: 30, scale: 2 }).notNull(),
  currencyId: int("currency_id", { unsigned: true })
    .notNull()
    .references(() => currencies.id, { onDelete: "cascade", onUpdate: "cascade" }),
  defaultCurrencyId: int("default_currency_id", { unsigned: true }).references(
    () => currencies.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  exchangeRate: double("exchange_rate"),
  projectId: int("project_id", { unsigned: true }), // no FK in legacy (plain column, not even indexed)
  bill: varchar("bill", { length: 255 }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  status: mysqlEnum("status", ["pending", "approved", "rejected"]).default("pending").notNull(),
  canClaim: boolean("can_claim").default(true).notNull(),
  categoryId: bigint("category_id", { mode: "number", unsigned: true }).references(
    () => expensesCategory.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  expensesRecurringId: bigint("expenses_recurring_id", {
    mode: "number",
    unsigned: true,
  }).references(() => expensesRecurring.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
    onDelete: "no action",
    onUpdate: "cascade",
  }),
  description: longtext("description"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  approverId: int("approver_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  bankAccountId: int("bank_account_id", { unsigned: true }).references(
    (): AnyMySqlColumn => bankAccounts.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** expenses_category_roles — pivot expenses_category ↔ roles. */
export const expensesCategoryRoles = mysqlTable("expenses_category_roles", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  expensesCategoryId: bigint("expenses_category_id", { mode: "number", unsigned: true }).references(
    () => expensesCategory.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  roleId: int("role_id", { unsigned: true }).notNull(), // FK → roles (deferred — rbac.ts)
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── PAYMENTS ─────────────────────────── */

export const payments = mysqlTable(
  "payments",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }), // FK → projects
    invoiceId: int("invoice_id", { unsigned: true }).references(() => invoices.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    orderId: bigint("order_id", { mode: "number", unsigned: true }).references(() => orders.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }), // FK → orders
    creditNotesId: int("credit_notes_id", { unsigned: true }).references(() => creditNotes.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    amount: double("amount").notNull(),
    gateway: varchar("gateway", { length: 255 }),
    transactionId: varchar("transaction_id", { length: 255 }),
    currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    defaultCurrencyId: int("default_currency_id", { unsigned: true }).references(
      () => currencies.id,
      { onDelete: "cascade", onUpdate: "cascade" },
    ),
    exchangeRate: double("exchange_rate"),
    planId: varchar("plan_id", { length: 255 }),
    customerId: varchar("customer_id", { length: 255 }),
    eventId: varchar("event_id", { length: 255 }),
    status: mysqlEnum("status", ["complete", "pending", "failed"]).default("pending").notNull(),
    paidOn: datetime("paid_on"),
    remarks: text("remarks"),
    offlineMethodId: int("offline_method_id", { unsigned: true }).references(
      () => offlinePaymentMethods.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    bill: varchar("bill", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    paymentGatewayResponse: text("payment_gateway_response"),
    payloadId: varchar("payload_id", { length: 255 }),
    bankAccountId: int("bank_account_id", { unsigned: true }).references(
      (): AnyMySqlColumn => bankAccounts.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("payments_paid_on_index").on(t.paidOn)],
);

/* ─────────────────────────── PRODUCTS ─────────────────────────── */

/** product_category — NO company_id (legacy). */
export const productCategory = mysqlTable("product_category", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  categoryName: varchar("category_name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const productSubCategory = mysqlTable("product_sub_category", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  categoryId: bigint("category_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => productCategory.id, { onDelete: "cascade", onUpdate: "cascade" }),
  categoryName: varchar("category_name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const products = mysqlTable("products", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  price: varchar("price", { length: 255 }).notNull(),
  taxes: varchar("taxes", { length: 255 }),
  allowPurchase: boolean("allow_purchase").default(false).notNull(),
  downloadable: boolean("downloadable").default(false).notNull(),
  downloadableFile: varchar("downloadable_file", { length: 255 }),
  description: text("description"),
  categoryId: bigint("category_id", { mode: "number", unsigned: true }).references(
    () => productCategory.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  subCategoryId: bigint("sub_category_id", { mode: "number", unsigned: true }).references(
    () => productSubCategory.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  hsnSacCode: varchar("hsn_sac_code", { length: 255 }),
  defaultImage: varchar("default_image", { length: 255 }),
  unitId: bigint("unit_id", { mode: "number", unsigned: true }).references(() => unitTypes.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** product_files — NO company_id, NO timestamps (legacy). */
export const productFiles = mysqlTable("product_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  productId: int("product_id", { unsigned: true })
    .notNull()
    .references(() => products.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 200 }),
  hashname: varchar("hashname", { length: 200 }),
  size: varchar("size", { length: 200 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
});

/* ─────────────────────────── PAYMENT GATEWAY CREDENTIALS ─────────────────────────── */

export const paymentGatewayCredentials = mysqlTable("payment_gateway_credentials", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  paypalClientId: varchar("paypal_client_id", { length: 255 }),
  paypalSecret: varchar("paypal_secret", { length: 255 }),
  paypalStatus: mysqlEnum("paypal_status", ["active", "deactive"]).default("deactive").notNull(),
  liveStripeClientId: varchar("live_stripe_client_id", { length: 255 }),
  liveStripeSecret: varchar("live_stripe_secret", { length: 255 }),
  liveStripeWebhookSecret: varchar("live_stripe_webhook_secret", { length: 255 }),
  stripeStatus: mysqlEnum("stripe_status", ["active", "deactive"]).default("deactive").notNull(),
  liveRazorpayKey: varchar("live_razorpay_key", { length: 255 }),
  liveRazorpaySecret: varchar("live_razorpay_secret", { length: 255 }),
  razorpayStatus: mysqlEnum("razorpay_status", ["active", "inactive"]).default("inactive").notNull(),
  paypalMode: mysqlEnum("paypal_mode", ["sandbox", "live"]).default("sandbox").notNull(),
  sandboxPaypalClientId: varchar("sandbox_paypal_client_id", { length: 255 }),
  sandboxPaypalSecret: varchar("sandbox_paypal_secret", { length: 255 }),
  testStripeClientId: varchar("test_stripe_client_id", { length: 255 }),
  testStripeSecret: varchar("test_stripe_secret", { length: 255 }),
  testRazorpayKey: varchar("test_razorpay_key", { length: 255 }),
  testRazorpaySecret: varchar("test_razorpay_secret", { length: 255 }),
  testStripeWebhookSecret: varchar("test_stripe_webhook_secret", { length: 255 }),
  stripeMode: mysqlEnum("stripe_mode", ["test", "live"]).default("test").notNull(),
  razorpayMode: mysqlEnum("razorpay_mode", ["test", "live"]).default("test").notNull(),
  paystackKey: varchar("paystack_key", { length: 255 }),
  paystackSecret: varchar("paystack_secret", { length: 255 }),
  paystackMerchantEmail: varchar("paystack_merchant_email", { length: 255 }),
  paystackStatus: mysqlEnum("paystack_status", ["active", "deactive"]).default("deactive"),
  paystackMode: mysqlEnum("paystack_mode", ["sandbox", "live"]).default("sandbox").notNull(),
  testPaystackKey: varchar("test_paystack_key", { length: 255 }),
  testPaystackSecret: varchar("test_paystack_secret", { length: 255 }),
  testPaystackMerchantEmail: varchar("test_paystack_merchant_email", { length: 255 }),
  paystackPaymentUrl: varchar("paystack_payment_url", { length: 255 }).default(
    "https://api.paystack.co",
  ),
  mollieApiKey: varchar("mollie_api_key", { length: 255 }),
  mollieStatus: mysqlEnum("mollie_status", ["active", "deactive"]).default("deactive"),
  payfastMerchantId: varchar("payfast_merchant_id", { length: 255 }),
  payfastMerchantKey: varchar("payfast_merchant_key", { length: 255 }),
  payfastPassphrase: varchar("payfast_passphrase", { length: 255 }),
  payfastMode: mysqlEnum("payfast_mode", ["sandbox", "live"]).default("sandbox").notNull(),
  payfastStatus: mysqlEnum("payfast_status", ["active", "deactive"]).default("deactive"),
  authorizeApiLoginId: varchar("authorize_api_login_id", { length: 255 }),
  authorizeTransactionKey: varchar("authorize_transaction_key", { length: 255 }),
  authorizeEnvironment: mysqlEnum("authorize_environment", ["sandbox", "live"])
    .default("sandbox")
    .notNull(),
  authorizeStatus: mysqlEnum("authorize_status", ["active", "deactive"])
    .default("deactive")
    .notNull(),
  squareApplicationId: varchar("square_application_id", { length: 255 }),
  squareAccessToken: varchar("square_access_token", { length: 255 }),
  squareLocationId: varchar("square_location_id", { length: 255 }),
  squareEnvironment: mysqlEnum("square_environment", ["sandbox", "production"])
    .default("sandbox")
    .notNull(),
  squareStatus: mysqlEnum("square_status", ["active", "deactive"]).default("deactive").notNull(),
  flutterwaveStatus: mysqlEnum("flutterwave_status", ["active", "deactive"])
    .default("deactive")
    .notNull(),
  flutterwaveMode: mysqlEnum("flutterwave_mode", ["sandbox", "live"]).default("sandbox").notNull(),
  testFlutterwaveKey: varchar("test_flutterwave_key", { length: 255 }),
  testFlutterwaveSecret: varchar("test_flutterwave_secret", { length: 255 }),
  testFlutterwaveHash: varchar("test_flutterwave_hash", { length: 255 }),
  liveFlutterwaveKey: varchar("live_flutterwave_key", { length: 255 }),
  liveFlutterwaveSecret: varchar("live_flutterwave_secret", { length: 255 }),
  liveFlutterwaveHash: varchar("live_flutterwave_hash", { length: 255 }),
  flutterwaveWebhookSecretHash: varchar("flutterwave_webhook_secret_hash", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── BANK ACCOUNTS ─────────────────────────── */

export const bankAccounts = mysqlTable("bank_accounts", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  type: varchar("type", { length: 255 }),
  bankName: varchar("bank_name", { length: 255 }),
  accountName: varchar("account_name", { length: 255 }),
  accountNumber: varchar("account_number", { length: 255 }),
  accountType: varchar("account_type", { length: 255 }),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  contactNumber: varchar("contact_number", { length: 255 }),
  openingBalance: double("opening_balance", { precision: 30, scale: 2 }),
  bankLogo: varchar("bank_logo", { length: 255 }),
  status: boolean("status"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  bankBalance: double("bank_balance", { precision: 30, scale: 2 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const bankTransactions = mysqlTable("bank_transactions", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  bankAccountId: int("bank_account_id", { unsigned: true }).references(() => bankAccounts.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  paymentId: int("payment_id", { unsigned: true }).references(() => payments.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  invoiceId: int("invoice_id", { unsigned: true }).references(() => invoices.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  expenseId: int("expense_id", { unsigned: true }).references(() => expenses.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  amount: double("amount", { precision: 30, scale: 2 }),
  type: mysqlEnum("type", ["Cr", "Dr"]).default("Cr").notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  memo: text("memo"),
  transactionRelation: varchar("transaction_relation", { length: 255 }),
  transactionRelatedTo: varchar("transaction_related_to", { length: 255 }),
  title: text("title"),
  transactionDate: date("transaction_date"),
  bankBalance: double("bank_balance", { precision: 30, scale: 2 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/* ─────────────────────────── CONTRACTS ─────────────────────────── */

export const contractTypes = mysqlTable("contract_types", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const contracts = mysqlTable("contracts", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  clientId: int("client_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }), // FK → projects
  subject: varchar("subject", { length: 255 }).notNull(),
  amount: varchar("amount", { length: 255 }).notNull(),
  originalAmount: decimal("original_amount", { precision: 15, scale: 0 }).notNull(),
  contractTypeId: bigint("contract_type_id", { mode: "number", unsigned: true }).references(
    () => contractTypes.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  startDate: date("start_date").notNull(),
  originalStartDate: date("original_start_date").notNull(),
  endDate: date("end_date"),
  originalEndDate: date("original_end_date"),
  description: longtext("description"),
  contractName: varchar("contract_name", { length: 255 }),
  // company_logo DROPPED by 2023_01_09 — omitted
  alternateAddress: varchar("alternate_address", { length: 255 }),
  contractNote: text("contract_note"),
  cell: varchar("cell", { length: 255 }),
  office: varchar("office", { length: 255 }),
  city: varchar("city", { length: 255 }),
  state: varchar("state", { length: 255 }),
  country: varchar("country", { length: 255 }),
  postalCode: varchar("postal_code", { length: 255 }),
  contractDetail: longtext("contract_detail"),
  contractNumber: varchar("contract_number", { length: 255 }),
  originalContractNumber: varchar("original_contract_number", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  hash: text("hash"),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  eventId: text("event_id"),
  companySign: varchar("company_sign", { length: 255 }),
  signDate: datetime("sign_date"),
  signBy: int("sign_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const contractDiscussions = mysqlTable("contract_discussions", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  contractId: bigint("contract_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => contracts.id, { onDelete: "cascade", onUpdate: "cascade" }),
  from: int("from", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  message: longtext("message").notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const contractFiles = mysqlTable("contract_files", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  contractId: bigint("contract_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => contracts.id, { onDelete: "cascade", onUpdate: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  hashname: varchar("hashname", { length: 255 }).notNull(),
  size: varchar("size", { length: 255 }).notNull(),
  googleUrl: varchar("google_url", { length: 255 }).notNull(),
  dropboxLink: varchar("dropbox_link", { length: 255 }).notNull(),
  externalLinkName: varchar("external_link_name", { length: 255 }).notNull(),
  externalLink: varchar("external_link", { length: 255 }).notNull(),
  description: text("description"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** contract_renews — legacy table name is `contract_renews`. */
export const contractRenews = mysqlTable("contract_renews", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  renewedBy: int("renewed_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  contractId: bigint("contract_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => contracts.id, { onDelete: "cascade", onUpdate: "cascade" }),
  startDate: date("start_date").notNull(),
  endDate: date("end_date").notNull(),
  amount: decimal("amount", { precision: 12, scale: 0 }).notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** contract_signs — legacy table name is `contract_signs`. NO company_id (legacy). */
export const contractSigns = mysqlTable("contract_signs", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  contractId: bigint("contract_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => contracts.id, { onDelete: "cascade", onUpdate: "cascade" }),
  fullName: varchar("full_name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  signature: varchar("signature", { length: 255 }).notNull(),
  place: varchar("place", { length: 255 }),
  date: varchar("date", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** contract_templates — $table->id() bigIncrements. */
export const contractTemplates = mysqlTable("contract_templates", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references((): AnyMySqlColumn => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  subject: varchar("subject", { length: 255 }).notNull(),
  description: longtext("description"),
  amount: varchar("amount", { length: 255 }).notNull(),
  contractTypeId: bigint("contract_type_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => contractTypes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  currencyId: int("currency_id", { unsigned: true }).references(() => currencies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  contractDetail: longtext("contract_detail"),
  addedBy: int("added_by").default(1).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
